import React from 'react';

import { CardHcd } from '@maiia/model/generated/model/api-patient/api-patient';
import dayjs from 'dayjs';

import { Box, Stack, Typography, Link } from '@mui/material';
import { CardSection, IconInfoRoundFilled } from '@maiia/design-system';
import { Heading } from '@kiiwi/ui';

import { useTranslation } from '@/src/i18n';
import Tarif from './Tarif';

type Props = {
  card: CardHcd;
};

const fixExternalLink = (link: string) => {
  // We only support http protocol, in future i dont know, i guess it will be httpxxxxx :D
  if (
    link.startsWith('http://') ||
    link.startsWith('https://') ||
    link.startsWith('//')
  )
    return link;
  return `http://${link}`;
};

const Information = ({ card }: Props) => {
  const { t } = useTranslation();

  const officeDescription = card.publicInformation?.officeDescription || '';
  const spokenLanguages = card.publicInformation?.spokenLanguages || [];
  const curriculums = card.publicInformation?.curriculums || [];
  const externalLink = card.publicInformation?.externalLink as
    | string
    | undefined;

  return (
    <CardSection
      icon={<IconInfoRoundFilled />}
      title={t('informations')}
      data-testid="information-section"
    >
      <Stack spacing={1}>
        {officeDescription?.length > 0 && (
          <div className="message text-pre">{officeDescription}</div>
        )}

        {externalLink && (
          <Box>
            <Typography component="h3" variant="h6">
              {t('site_internet')}
            </Typography>
            <Link
              href={fixExternalLink(externalLink)}
              rel="noopener noreferrer"
              target="_blank"
            >
              {t('visit_prat_site')}
            </Link>
          </Box>
        )}

        {spokenLanguages.length > 0 && (
          <Box data-testid="spoken-languages-section">
            <Typography component="h3" variant="h6">
              {t('spoken_languages')}
            </Typography>
            <p>
              {spokenLanguages
                .map(language => t(`language_${language}`))
                .join(', ')}
            </p>
          </Box>
        )}

        {curriculums.length > 0 && (
          <Box data-testid="diplomas-section">
            <Heading as="h4" variant="subtitle-3" className="font-bold">
              {t('curriculum_and_diplomas')}
            </Heading>
            <ul>
              {curriculums.map((curriculum, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <li className="cv-elem" key={`curriculum_${index}`}>
                  <span className="cv-type">{`${curriculum.degree}`}</span>
                  <span className="cv-date">
                    &nbsp;
                    {`(${dayjs(curriculum.graduationDate).format('YYYY')})`}
                  </span>
                </li>
              ))}
            </ul>
          </Box>
        )}

        {/* For practitioners, the <Tarif /> is displayed in <Payback /> */}
        {card?.type !== 'PRACTITIONER' && <Tarif card={card} />}
      </Stack>
    </CardSection>
  );
};

export default Information;
